/**
 * 資料エディタ 中央パネル — セクションリスト + DnD 並び替え + 追加/削除/編集。
 */
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DocumentSection } from '@goenhub/shared';
import {
  Bookmark,
  Copy,
  Eye,
  EyeOff,
  GripVertical,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import {
  useUpsertDocument,
  type DocumentRecord,
} from '@/hooks/useDocuments';

import { getSection, getVariant } from '../sectionRegistry';
import { DocumentAddSectionDialog } from './DocumentAddSectionDialog';
import { DocumentSaveTemplateDialog } from './DocumentSaveTemplateDialog';

interface Props {
  document: DocumentRecord;
  activeSectionId: string | null;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
}

function sectionTitle(section: DocumentSection): string {
  const cfg = (section.config ?? {}) as Record<string, unknown>;
  const pick = (...keys: string[]): string => {
    for (const k of keys) {
      const v = cfg[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return '';
  };
  switch (section.type) {
    case 'hero':
      return pick('title', 'subtitle') || '(無題)';
    case 'text': {
      const html = ((cfg.html as string) ?? '').replace(/<[^>]+>/g, '').trim();
      return html.slice(0, 40) || pick('title') || '(本文未入力)';
    }
    case 'image':
      return pick('caption') || '(画像)';
    case 'cta':
      return pick('title', 'label') || '(CTA)';
    case 'comparison_table':
      return pick('title') || '機能比較';
    case 'feature_grid':
      return pick('title') || '特徴';
    case 'specifications':
      return pick('title') || '製品スペック';
    case 'pricing_plans':
      return pick('title') || '料金プラン';
    case 'process_steps':
      return pick('title') || 'ステップフロー';
    case 'testimonials':
      return pick('title') || 'お客様の声';
    case 'case_studies':
      return pick('title') || '導入事例';
    case 'faq':
      return pick('title') || 'よくある質問';
    case 'lease_terms':
      return pick('title') || 'リース条件';
  }
}

export function DocumentPageOutline({
  document,
  activeSectionId,
  onSelect,
  onEdit,
}: Props) {
  const sections = document.sections ?? [];
  const [addOpen, setAddOpen] = useState(false);
  const [tplSection, setTplSection] = useState<DocumentSection | null>(null);
  const upsert = useUpsertDocument();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const persistSections = async (next: DocumentSection[]) => {
    try {
      await upsert.mutateAsync({
        id: document.id,
        title: document.title,
        slug: document.slug,
        status: document.status,
        sections: next,
        autoReplyTemplate: document.autoReplyTemplate ?? null,
      });
    } catch (e) {
      toast.error(`保存失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex((s) => s.id === active.id);
    const newIdx = sections.findIndex((s) => s.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(sections, oldIdx, newIdx);
    void persistSections(reordered);
  };

  const handleDelete = async (id: string) => {
    const target = sections.find((s) => s.id === id);
    if (!target) return;
    const def = getSection(target.type);
    if (!confirm(`「${def?.label ?? target.type}」セクションを削除しますか？`)) return;
    await persistSections(sections.filter((s) => s.id !== id));
    toast.success('セクションを削除しました');
  };

  const handleToggleHidden = async (id: string) => {
    const next = sections.map((s) =>
      s.id === id ? { ...s, hidden: !s.hidden } : s,
    );
    await persistSections(next);
  };

  const handleDuplicate = async (id: string) => {
    const idx = sections.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const source = sections[idx];
    const duplicated: DocumentSection = {
      ...source,
      // config はディープコピー（参照共有でバグらせない）
      config: JSON.parse(JSON.stringify(source.config ?? {})),
      id: `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    };
    const next = [
      ...sections.slice(0, idx + 1),
      duplicated,
      ...sections.slice(idx + 1),
    ];
    await persistSections(next);
    toast.success('セクションを複製しました');
  };

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold">ページ構成</div>
          <div className="text-[11px] text-[var(--text-muted)]">
            {sections.length} セクション
          </div>
        </div>
      </div>

      {sections.length === 0 && (
        <div className="text-xs text-[var(--text-muted)] p-3 border border-dashed border-[var(--border)] rounded-md text-center">
          まだセクションがありません。下のボタンから追加してください。
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sections.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {sections.map((s, idx) => (
              <SectionCard
                key={s.id}
                index={idx + 1}
                section={s}
                isActive={s.id === activeSectionId}
                onSelect={() => onSelect(s.id)}
                onEdit={() => onEdit(s.id)}
                onDelete={() => handleDelete(s.id)}
                onToggleHidden={() => handleToggleHidden(s.id)}
                onDuplicate={() => handleDuplicate(s.id)}
                onSaveTemplate={() => setTplSection(s)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button
        type="button"
        onClick={() => setAddOpen(true)}
        className="w-full text-xs text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-md py-2 hover:bg-[var(--bg-hover)]"
      >
        <Plus className="size-3 inline mr-1" />
        セクションを追加
      </button>

      <DocumentAddSectionDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        document={document}
        onAdded={(id) => {
          onSelect(id);
          onEdit(id);
        }}
      />

      <DocumentSaveTemplateDialog
        open={!!tplSection}
        onOpenChange={(o) => {
          if (!o) setTplSection(null);
        }}
        section={tplSection}
        sectionLabel={
          tplSection ? getSection(tplSection.type)?.label ?? tplSection.type : undefined
        }
      />
    </div>
  );
}

interface CardProps {
  index: number;
  section: DocumentSection;
  isActive: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleHidden: () => void;
  onDuplicate: () => void;
  onSaveTemplate: () => void;
}

function SectionCard({
  index,
  section,
  isActive,
  onSelect,
  onEdit,
  onDelete,
  onToggleHidden,
  onDuplicate,
  onSaveTemplate,
}: CardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: section.id });
  // ドラッグ中は opacity 0.5、非表示は 0.45、それ以外は 1
  const baseOpacity = section.hidden ? 0.45 : 1;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : baseOpacity,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-2 rounded-md border transition-colors cursor-pointer ${
        isActive
          ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
          : 'border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)]'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab text-[var(--text-muted)] mt-1"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="size-4" />
        </button>
        <div className="text-[11px] text-[var(--text-muted)] font-mono w-6">
          {String(index).padStart(2, '0')}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold text-[var(--text-muted)] mb-0.5 flex items-center gap-1">
            <span>{getSection(section.type)?.label ?? section.type}</span>
            {section.variant && (
              <>
                <span>·</span>
                <span className="text-[var(--text-muted)]">
                  {getVariant(section.type, section.variant)?.variant?.label ??
                    section.variant}
                </span>
              </>
            )}
          </div>
          <div className="text-xs font-medium truncate">
            {sectionTitle(section)}
          </div>
        </div>
      </div>
      {isActive && (
        <div className="mt-2 flex items-center gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <Pencil className="size-3" />
            <span className="ml-1">編集</span>
          </Button>
          <div className="ml-auto flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
              className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              title="このセクションを複製"
            >
              <Copy className="size-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onToggleHidden();
              }}
              className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              title={section.hidden ? '公開ページに表示する' : '公開ページから非表示にする'}
            >
              {section.hidden ? (
                <EyeOff className="size-3" />
              ) : (
                <Eye className="size-3" />
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onSaveTemplate();
              }}
              className="text-[var(--text-muted)] hover:text-[var(--accent)]"
              title="このセクションをテンプレートとして保存"
            >
              <Bookmark className="size-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="text-[var(--text-muted)] hover:text-red-500"
              title="このセクションを削除"
            >
              <Trash2 className="size-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
